import { MAX_PRODUCT_EXCLUSIONS, type UpdateExcludedProductsRequest } from '@avrahami/contracts';
import { Alert, Card, ConfirmDialog, fill, Loading } from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { excludedProductsQuery, keys, platformApi } from '../api/platform';
import { he } from '../i18n/he';
import {
  exclusionChangeText,
  exclusionCountText,
  exclusionErrorText,
  exclusionRoom,
  visibilityTitle,
  type PickedProduct,
  type VisibilityChange,
} from '../lib/exclusions';
import { errorMessage } from '../lib/format';
import { StoreProductsTable } from './StoreProductsTable';

/**
 * The catalog's products as a customer store gets them (adr/0023, adr/0047): a table of the
 * catalog where every product is shown in the store or not, by default as the original site shows
 * the store's customer, and otherwise by the platform's choice. Staff who may create stores change it,
 * a product at a time or the ticked ones together; the whole catalog shows for staff who may
 * read it, the others see the products that are not shown.
 */
export function TenantExclusionsCard({
  tenantId,
  canChange,
  canPick,
}: {
  tenantId: string;
  canChange: boolean;
  /** The table reads the master catalog (`platform.catalog.read`). */
  canPick: boolean;
}) {
  const queryClient = useQueryClient();
  const excluded = useQuery(excludedProductsQuery(tenantId));
  const [selected, setSelected] = useState<readonly PickedProduct[]>([]);
  /** The change the dialog asks about. */
  const [asked, setAsked] = useState<VisibilityChange | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: (body: UpdateExcludedProductsRequest) =>
      platformApi.updateExcludedProducts(tenantId, body),
    onSuccess: async (result) => {
      const { added, lifted, addedToStore, items, shownByChoice } = result;
      queryClient.setQueryData(keys.exclusions(tenantId), { items, shownByChoice });
      setAsked(null);
      setSelected([]);
      setDone(exclusionChangeText({ added, lifted, addedToStore }));
      // Products left the store (or may return to it): its counts and its log changed.
      await Promise.all(
        [keys.tenantAi(tenantId), keys.tenantCopy(tenantId), keys.audit(tenantId)].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });

  const items = excluded.data?.items ?? [];

  return (
    <Card title={he.exclusions.title}>
      <div className="flex flex-col gap-4" data-testid="tenant-exclusions">
        <p className="text-sm text-slate-600">{he.exclusions.intro}</p>
        {done === null ? null : (
          <div data-testid="tenant-exclusions-notice">
            <Alert tone="success">{done}</Alert>
          </div>
        )}
        {excluded.isPending ? <Loading /> : null}
        {excluded.isError ? (
          <Alert>{errorMessage(excluded.error, { FORBIDDEN: he.exclusions.viewForbidden })}</Alert>
        ) : null}
        {excluded.isSuccess ? (
          <>
            <p className="text-sm font-medium text-slate-900" data-testid="tenant-exclusions-count">
              {items.length === 0 ? he.exclusions.empty : exclusionCountText(items.length)}
            </p>
            <StoreProductsTable
              name={`store-products:${tenantId}`}
              hidden={items}
              shownByChoice={excluded.data.shownByChoice}
              selected={selected}
              onSelect={setSelected}
              onChange={(next) => {
                setDone(null);
                change.reset();
                setAsked(next);
              }}
              canBrowse={canPick}
              canChange={canChange}
              disabled={change.isPending}
            />
          </>
        ) : null}
        {canChange && exclusionRoom(items.length) === 0 ? (
          <p className="text-xs text-slate-500">
            {fill(he.exclusions.tooMany, { max: MAX_PRODUCT_EXCLUSIONS })}
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={asked !== null}
        title={asked === null ? '' : visibilityTitle(asked)}
        confirmLabel={asked?.shown === true ? he.exclusions.table.show : he.exclusions.table.hide}
        confirmTestId="tenant-exclusions-confirm"
        busy={change.isPending}
        onConfirm={() => {
          if (asked === null) return;
          const ids = asked.products.map((product) => product.id);
          change.mutate(asked.shown ? { add: [], remove: ids } : { add: ids, remove: [] });
        }}
        onClose={() => {
          setAsked(null);
          change.reset();
        }}
      >
        <div className="flex flex-col gap-3">
          {asked?.products.length === 1 ? (
            <p className="font-medium text-slate-900">{asked.products[0]?.name}</p>
          ) : null}
          <p>{asked?.shown === true ? he.exclusions.showBody : he.exclusions.hideBody}</p>
          {change.isError ? <Alert>{exclusionErrorText(change.error)}</Alert> : null}
        </div>
      </ConfirmDialog>
    </Card>
  );
}
